import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import {
  createFormTaxonomy,
  fetchFormTaxonomiesList,
} from '../features/formTaxonomies/formTaxonomiesApi';
import { taxonomyCodeFromName } from '../features/formTaxonomies/formTaxonomyUtils';
import type { FormTaxonomyFlat } from '../features/formTaxonomies/types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

function parentLabel(taxonomies: FormTaxonomyFlat[], parentId: string | null): string {
  if (!parentId) return '—';
  return taxonomies.find((t) => t.id === parentId)?.name ?? '—';
}

export function FormTaxonomiesPage() {
  const { canEdit } = usePlatformAccessLevel('form_templates.access');
  const [taxonomies, setTaxonomies] = useState<FormTaxonomyFlat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [parentId, setParentId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchFormTaxonomiesList();
      setTaxonomies(data.filter((t) => t.active));
    } catch {
      setError('No se pudieron cargar las taxonomías.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(
    () => [...taxonomies].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)),
    [taxonomies],
  );

  const resetForm = () => {
    setName('');
    setCode('');
    setParentId('');
    setFormError(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setFormError(null);
    try {
      await createFormTaxonomy({
        name: name.trim(),
        code: code.trim() || undefined,
        parentId: parentId || null,
      });
      setDialogOpen(false);
      resetForm();
      await load();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'No se pudo crear la taxonomía.';
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/libros" className="text-sm text-primary hover:underline">
            ← Libros
          </Link>
          <h1 className="text-2xl font-semibold mt-1">Carpetas del menú (taxonomías)</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Las taxonomías son las carpetas del menú lateral bajo <strong>Formularios</strong>.
            Agrupan plantillas y libros anclados. Necesitás al menos una para anclar un libro.
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={openCreate}
          >
            + Nueva carpeta
          </button>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Nombre</th>
              <th className="px-3 py-2 text-left font-medium">Código</th>
              <th className="px-3 py-2 text-left font-medium">Carpeta padre</th>
              <th className="px-3 py-2 text-left font-medium">Orden</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">
                  Sin carpetas. {canEdit ? 'Creá la primera con «Nueva carpeta».' : 'Pedí a un administrador que cree una.'}
                </td>
              </tr>
            )}
            {!loading &&
              sorted.map((t) => (
                <tr key={t.id} className="border-t hover:bg-muted/40">
                  <td className="px-3 py-2 font-medium">{t.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{t.code}</td>
                  <td className="px-3 py-2">{parentLabel(taxonomies, t.parentId)}</td>
                  <td className="px-3 py-2 tabular-nums">{t.order}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nueva carpeta del menú</DialogTitle>
          </DialogHeader>
          <form className="space-y-3" onSubmit={(e) => void handleSubmit(e)}>
            <label className="block text-sm">
              Nombre visible
              <input
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!code.trim()) {
                    setCode(taxonomyCodeFromName(e.target.value));
                  }
                }}
                placeholder="Ej: Calidad, Compras, Indicadores"
                maxLength={255}
                required
              />
            </label>
            <label className="block text-sm">
              Código interno (opcional)
              <input
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm font-mono"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Se genera automáticamente"
                maxLength={64}
              />
            </label>
            <label className="block text-sm">
              Carpeta padre (opcional)
              <select
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
              >
                <option value="">Raíz del menú</option>
                {sorted.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="rounded-md border px-3 py-2 text-sm"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-50"
                disabled={submitting || !name.trim()}
              >
                {submitting ? 'Guardando…' : 'Crear'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
